/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Injectable, Type } from "@angular/core";
import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  FnTransferableArg,
  UiArguments,
} from "shared/provider-webworker";

import { ILookupService } from "../ILookupService";
import { PromiseLookupService } from "./PromiseLookupService";
import { BaseLookupService } from "./BaseLookupService";
import { Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Serializer } from "shared/provider-webworker";

export abstract class ReplicaLookupService<
  K,
  V extends CubiqTS.ConcreteEncodable,
> extends PromiseLookupService<K, V> {
  protected broker: ClientMessageBroker;
  protected readonly VALUE: CubiqTS.ConcreteEncodable | CubiqTS.ConcreteEncodable[];
  protected readonly EVENT_VALUE: CubiqTS.ConcreteEncodable;
  protected _serializer: Serializer = new CubiqTS.CubiqSerializer();
  protected modelUpdateEventSubject: Subject<CubiqTS.ConcreteEncodable> =
    new Subject<CubiqTS.ConcreteEncodable>();
  protected base: BaseLookupService<K, V> = null;

  /**
   * Default constructor
   *
   * @param factory
   * @param lookupType
   * @param serviceChannel
   * @param modelUpdateEventType the type of the model update event
   * @param autoSubscribeUpdates indicates if the replica should subscribe to and redistribute model update events
   * to local subscribers
   * @param baseLookupService a base lookup service implementation that will serve as a local replica of the base lookup service.
   * If one is assigned, priming will be done regardless of the value of autoSubscribeUpdates flag and all model update events
   * will be used to update the assigned implementation
   */
  constructor(
    protected factory: ClientMessageBrokerFactory,
    protected lookupType: Type<CubiqTS.ConcreteEncodable> | Array<Type<CubiqTS.ConcreteEncodable>>,
    protected serviceChannel: string,
    protected modelUpdateEventType: Type<CubiqTS.ConcreteEncodable> = CubiqTS.ModelUpdateImpl,
    protected autoSubscribeUpdates: boolean = true,
    protected baseLookupService: BaseLookupService<K, V> = null,
  ) {
    super();

    this.VALUE = Array.isArray(lookupType)
      ? CubiqTS.MIXED_ENCODABLE(lookupType)
      : CubiqTS.ENCODABLE(lookupType);
    this.EVENT_VALUE = CubiqTS.ENCODABLE(modelUpdateEventType);

    this.broker = this.factory.createMessageBroker(this.serviceChannel, true, true);

    if (baseLookupService == null && autoSubscribeUpdates) {
      logger.out.info(`${this.constructor.name}: init model update subject without base `);
      this.initModelUpdateService();
    } else if (baseLookupService != null) {
      logger.out.info(
        `${this.constructor.name}: init model update subject base ${baseLookupService.constructor.name} `,
      );

      this.primeBaseLookupService(baseLookupService).then(
        (primed) => {
          if (primed) {
            this.modelUpdateEventSubject.subscribe((event: CubiqTS.ModelUpdate) => {
              this.updateBaseLookupService(event);
            });

            this.initModelUpdateService();
          } else {
            logger.out.info(`${this.constructor.name}: not primed, not subscriping to updates`);
          }
        },
        (error) => {
          logger.out.info(
            `${this.constructor.name}: error priming, not subscriping to updates %O`,
            error,
          );
        },
      );
    } else {
      logger.out.info(`${this.constructor.name}: init with no updates`);
    }
  }

  /**
   * Invokes priming by a getAll call to the primary lookup service and than
   * feeding the obtained values to the assigned base lookup service
   * @param baseLookupService the base lookup service, must not be null at this point
   * @return a promise that will be resolved after all related mechanisms for populating and
   * updating the base lookup service have been executed.
   */
  protected primeBaseLookupService(baseLookupService: BaseLookupService<K, V>): Promise<boolean> {
    this.base = baseLookupService;

    return new Promise<boolean>((resolve, reject) => {
      this.isPrimed().then(
        (primed: boolean) => {
          if (!primed) {
            logger.out.error(
              `${this.constructor.name}: primeBaseLookupService isPrimed() ${primed}`,
            );
            resolve(false);
            return;
          }

          this.getAll().then(
            (values: V[]) => {
              baseLookupService.prime(values, true);
              logger.out.info(`${this.constructor.name}: primeBaseLookupService success.`);

              resolve(true);
            },
            (error: any) => {
              logger.out.error(
                `${this.constructor.name}: primeBaseLookupService error %O. ${baseLookupService.constructor.name} will be empty`,
                error,
              );
              resolve(false);
            },
          );
        },
        (error: any) => {
          logger.out.error(
            `${this.constructor.name}: primeBaseLookupService error %O. ${baseLookupService.constructor.name} will be empty`,
            error,
          );
          resolve(false);
        },
      );
    });
  }

  /**
   * Initializes model update capabilities of the replica service
   * by subscribing to update events from the primary lookup service
   * and relaying all model update events to local subscribers of the
   * model update event subject
   */
  protected initModelUpdateService(): void {
    let refreshChannel = new MessageChannel();
    let clientRefreshPort: MessagePort = refreshChannel.port1;
    let serviceRefreshPort: MessagePort = refreshChannel.port2;

    let request = new UiArguments("initModelUpdateService", [
      new FnTransferableArg(serviceRefreshPort, PRIMITIVE),
    ]);

    clientRefreshPort.addEventListener("message", (event: any) => {
      let modelUpdateEvent: CubiqTS.ModelUpdate = this._serializer.deserialize(
        event.data,
        this.EVENT_VALUE,
      );

      this.modelUpdateEventSubject.next(modelUpdateEvent);
    });

    this.broker.runOnService(request, null);
    clientRefreshPort.start();
  }

  /**
   * Update the replica base lookup implementation if
   * one has been assigned
   * @param event the model update/delete event
   */
  protected updateBaseLookupService(event: CubiqTS.ModelUpdate): void {
    if (
      event == Object(event) &&
      event.payLoad == Object(event.payLoad) &&
      event.payLoad instanceof (this.lookupType as any) &&
      this.base == Object(this.base)
    ) {
      let payload: V = event.payLoad as V;

      if (payload && event.eventType == CubiqTS.ModelUpdateType.DELETE) {
        this.base.deleteItem(payload);
      } else if (payload) {
        this.base.mapItem(payload);
      }
    }
  }

  isPrimed(): Promise<boolean> {
    let request = new UiArguments("isPrimed");
    request.args = [];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  prime(values: Array<V>): Promise<void> {
    let request = new UiArguments("prime");
    let arg1 = new FnArg(values, this.VALUE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  getItemKey(item: V): Promise<K> {
    let request = new UiArguments("getItemKey");
    let arg1 = new FnArg(item, this.VALUE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  getItem(id: K): Promise<V> {
    let request = new UiArguments("getItem");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, this.VALUE);
  }

  createItem(item: V): Promise<boolean> {
    let request = new UiArguments("createItem");
    let arg1 = new FnArg(item, this.VALUE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  updateItem(item: V): Promise<boolean> {
    let request = new UiArguments("updateItem");
    let arg1 = new FnArg(item, this.VALUE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  deleteItem(item: V): Promise<void> {
    let request = new UiArguments("deleteItem");
    let arg1 = new FnArg(item, this.VALUE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  deleteItemById(id: K): Promise<void> {
    let request = new UiArguments("deleteItemById");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, PRIMITIVE);
  }

  getAll(): Promise<Array<V>> {
    let request = new UiArguments("getAll");
    request.args = [];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupItem(propertyName: string, substring: string): Promise<Array<V>> {
    let request = new UiArguments("lookupItem");
    let arg1 = new FnArg(propertyName, PRIMITIVE);
    let arg2 = new FnArg(substring, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  getBase(): ILookupService<K, V> {
    // logger.out.info('base lookup unavailable in replica context (use will fail)');
    return this.base;
  }

  getModelUpdateSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelUpdateEventSubject;
  }
}
